import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import toast from 'react-hot-toast';
import { Analytics } from '../analytics';
import { useAuth } from '../hooks/useAuth';
import { useAutoList } from '../hooks/useAutoList';
import { backdropClose } from '../utils/modalBackdrop';

// Композер клиентского письма, привязанного к Работе: «Работа выполнена» (completion)
// и «Счёт на оплату» (invoice). Поля (услуга, дата, мастер, адрес, гарантия) тянутся
// из Работы автоматически и видны в превью; админ правит обращение, личное сообщение,
// подпись, кнопку, а для счёта — номер/сумму/срок оплаты. Дубль-защита: если письмо
// по этой Работе уже отправляли — баннер + подтверждение.
export default function ClientEmailComposer({ task, template, onClose, onSent }) {
  const { t, i18n } = useTranslation();
  const msgRef = useAutoList('client_email_message');
  const { user } = useAuth();
  const orgCur = user?.default_currency || 'AED';

  const initLang = (task && task.comm_lang === 'en') ? 'en'
    : (task && task.comm_lang === 'ru') ? 'ru'
    : (i18n.language === 'en' ? 'en' : 'ru');

  const [lang, setLang] = useState(initLang);
  const [name, setName] = useState(task?.client_name || '');
  const [customMessage, setCustomMessage] = useState('');
  const [signature, setSignature] = useState('');
  const [ctaOn, setCtaOn] = useState(false);       // кнопка действия по умолчанию выключена
  const [fromSelf, setFromSelf] = useState(false);
  // invoice-поля (нет авто-источника — заполняет админ)
  const [invNo, setInvNo] = useState('');
  const [amountDue, setAmountDue] = useState('');
  const [dueDate, setDueDate] = useState('');

  const [status, setStatus] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const isInvoice = template === 'invoice';
  const prevSend = status?.sends?.[template] || null;   // последняя отправка этого шаблона
  const senderInfo = status?.sender_info || null;

  const title = isInvoice ? t('clientEmail.titleInvoice') : t('clientEmail.titleCompletion');

  useEffect(() => {
    api.getTaskEmailStatus(task.id)
      .then(s => {
        setStatus(s);
        Analytics.clientEmailOpened({ task_id: task.id, template, already_sent: !!(s?.sends?.[template]) });
      })
      .catch(() => {});
    // eslint-disable-next-line
  }, []);

  // Прячем Action-FAB, пока открыт модал.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('fab-hide'));
    return () => window.dispatchEvent(new CustomEvent('fab-show'));
  }, []);

  const infoOverrides = useCallback(() => {
    const o = {};
    if (isInvoice && invNo.trim()) o.inv_no = invNo.trim();
    return o;
  }, [isInvoice, invNo]);

  // Дебаунс-загрузка превью.
  const debounceRef = useRef(null);
  const loadPreview = useCallback(() => {
    setPreviewLoading(true);
    api.getTaskEmailPreview(task.id, {
      template, lang,
      client_name: name,
      custom_message: customMessage,
      signature,
      info_overrides: infoOverrides(),
      amount_due: isInvoice ? amountDue : '',
      due_date: isInvoice ? dueDate : '',
      cta: ctaOn,
    })
      .then(r => setPreviewHtml(prev => (prev === (r.html || '') ? prev : (r.html || ''))))
      .catch(() => setPreviewHtml(''))
      .finally(() => setPreviewLoading(false));
  }, [task.id, template, lang, name, customMessage, signature, infoOverrides, isInvoice, amountDue, dueDate, ctaOn]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(loadPreview, 600);
    return () => clearTimeout(debounceRef.current);
  }, [loadPreview]);

  const doSend = async (force) => {
    setSending(true);
    try {
      const r = await api.sendTaskEmail(task.id, {
        template, lang,
        client_name: name,
        custom_message: customMessage,
        signature,
        info_overrides: infoOverrides(),
        amount_due: isInvoice ? amountDue : undefined,
        due_date: isInvoice ? dueDate : undefined,
        cta: ctaOn,
        from_self: fromSelf,
        force,
      });
      Analytics.clientEmailSent({ task_id: task.id, template, lang, forced: !!force });
      toast.success(t('clientEmail.sentToast', { email: r.sent_to }));
      onSent && onSent(r);
      onClose();
    } catch (e) {
      if (e.code === 'already_sent') {
        Analytics.clientEmailDuplicateWarned({ task_id: task.id, template });
        const who = e.sent_by_name || t('clientEmail.someone');
        if (window.confirm(t('clientEmail.confirmDuplicate', { name: who }))) {
          doSend(true);
          return;
        }
      } else if (e.code === 'no_email') {
        toast.error(t('clientEmail.noEmail'));
      } else {
        toast.error(e.message || t('clientEmail.sendError'));
      }
    } finally {
      setSending(false);
    }
  };

  const handleSendClick = () => {
    if (prevSend && !window.confirm(t('clientEmail.confirmDuplicate', { name: prevSend.sent_by_name || t('clientEmail.someone') }))) return;
    doSend(!!prevSend);
  };

  const labelStyle = {
    fontSize: 11, color: 'var(--text3)', fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.5px',
  };
  const hintStyle = { fontSize: 12, color: 'var(--text3)', marginTop: 4 };

  const fmtDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long' }); }
    catch { return iso; }
  };

  const noEmail = status && !status.has_email;

  return (
    <div className="modal-overlay" {...backdropClose(onClose)}>
      <div className="modal" style={{ maxWidth: 900, width: '94%' }}>
        <div className="modal-header">
          <span>{isInvoice ? '🧾' : '✅'} {title}</span>
          <button type="button" className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body" style={{ paddingTop: 12 }}>
          {noEmail && (
            <div style={{
              marginBottom: 16, padding: '10px 14px', borderRadius: 8,
              background: 'rgba(245,158,11,0.16)', border: '1px solid var(--orange, #f5a623)',
              color: 'var(--text)', fontSize: 13, lineHeight: 1.5,
            }}>
              ⚠️ {t('clientEmail.noEmail')}
            </div>
          )}

          {prevSend && (
            <div style={{
              marginBottom: 16, padding: '10px 14px', borderRadius: 8,
              background: 'rgba(245,158,11,0.16)', border: '1px solid var(--orange, #f5a623)',
              color: 'var(--text)', fontSize: 13, lineHeight: 1.5,
            }}>
              ⚠️ {t('clientEmail.alreadyWarning', {
                name: prevSend.sent_by_name || t('clientEmail.someone'),
                date: fmtDate(prevSend.sent_at),
              })}
            </div>
          )}

          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            {/* Левая колонка — параметры */}
            <div style={{ flex: '1 1 320px', minWidth: 280 }}>
              {/* Язык */}
              <div style={{ marginBottom: 16 }}>
                <div style={labelStyle}>{t('clientEmail.language')}</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                  {['ru', 'en'].map(l => (
                    <button key={l} type="button"
                      className={`btn btn-sm ${lang === l ? 'btn-green' : 'btn-ghost'}`}
                      onClick={() => setLang(l)}>
                      {l === 'ru' ? 'RU' : 'EN'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Обращение */}
              <div style={{ marginBottom: 16 }}>
                <div style={labelStyle}>{t('clientEmail.name')}</div>
                <input className="form-input" type="text" value={name}
                  onChange={e => setName(e.target.value)} maxLength={120} style={{ marginTop: 6 }} />
                <div style={hintStyle}>{t('clientEmail.nameHint')}</div>
              </div>

              {/* invoice: номер / сумма / срок */}
              {isInvoice && (
                <div style={{
                  marginBottom: 16, padding: '14px 16px', borderRadius: 8,
                  background: 'var(--bg2)', border: '1px solid var(--border)',
                  display: 'flex', flexDirection: 'column', gap: 12,
                }}>
                  <div>
                    <div style={labelStyle}>{t('clientEmail.invNo')}</div>
                    <input className="form-input" type="text" value={invNo}
                      onChange={e => setInvNo(e.target.value)} maxLength={60} style={{ marginTop: 6 }} />
                  </div>
                  <div>
                    <div style={labelStyle}>{t('clientEmail.amountDue')}</div>
                    <input className="form-input" type="text" value={amountDue}
                      onChange={e => setAmountDue(e.target.value)} maxLength={60}
                      placeholder={`1 500 ${orgCur}`} style={{ marginTop: 6 }} />
                  </div>
                  <div>
                    <div style={labelStyle}>{t('clientEmail.dueDate')}</div>
                    <input className="form-input" type="date" value={dueDate}
                      onChange={e => setDueDate(e.target.value)} style={{ marginTop: 6 }} />
                  </div>
                </div>
              )}

              {/* Личное сообщение */}
              <div style={{ marginBottom: 16 }}>
                <div style={labelStyle}>{t('clientEmail.customMessage')}</div>
                <textarea className="form-textarea" value={customMessage}
                  onChange={e => setCustomMessage(e.target.value)} rows={3} maxLength={2000}
                  ref={msgRef} data-ph-capture-attribute-field="client_email_message"
                  placeholder={t('clientEmail.customMessagePlaceholder')}
                  style={{ marginTop: 6, overflowX: 'hidden' }} />
              </div>

              {/* Подпись */}
              <div style={{ marginBottom: 16 }}>
                <div style={labelStyle}>{t('clientEmail.signature')}</div>
                <textarea className="form-textarea" value={signature}
                  onChange={e => setSignature(e.target.value)} rows={2} maxLength={200}
                  style={{ marginTop: 6, minHeight: 56, overflowX: 'hidden' }} />
              </div>

              {/* Кнопка действия */}
              <div style={{ marginTop: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={ctaOn} onChange={e => setCtaOn(e.target.checked)} />
                  <span style={{ fontWeight: 600 }}>{t('clientEmail.ctaBlock')}</span>
                </label>
              </div>

              {/* От моего ящика */}
              {senderInfo?.self_email && (
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={fromSelf} onChange={e => setFromSelf(e.target.checked)} />
                    <span style={{ fontWeight: 600 }}>{t('clientEmail.fromSelf')}</span>
                  </label>
                  <div style={hintStyle}>
                    {fromSelf
                      ? (senderInfo.eligible
                          ? `✉️ ${t('clientEmail.fromSelfOn', { from: senderInfo.self_from })}`
                          : `⚠️ ${t('clientEmail.fromSelfFallback', { email: senderInfo.self_email, from: senderInfo.default_from })}`)
                      : t('clientEmail.fromSelfHint', { from: senderInfo.default_from })}
                  </div>
                </div>
              )}
            </div>

            {/* Правая колонка — превью */}
            <div style={{ flex: '1 1 340px', minWidth: 300 }}>
              <div style={{ ...labelStyle, marginBottom: 6 }}>{t('clientEmail.preview')}</div>
              <div style={{
                border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden',
                height: 460, background: '#fff', position: 'relative',
              }}>
                {previewLoading && (
                  <div style={{
                    position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', color: 'var(--text3)', fontSize: 13,
                  }}>{t('clientEmail.previewLoading')}</div>
                )}
                <iframe title="preview" sandbox="" srcDoc={previewHtml}
                  style={{ width: '100%', height: '100%', border: 0 }} />
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={sending}>
            {t('common.cancel')}
          </button>
          <button type="button" className="btn btn-green" onClick={handleSendClick} disabled={sending || noEmail}>
            {sending ? t('clientEmail.sending') : t('clientEmail.send')}
          </button>
        </div>
      </div>
    </div>
  );
}
